import type { MeaningfulNoticingCard } from "@/components/coach/sessionPrep/MeaningfulNoticingSection";
import type {
  CoachSessionPrepBehavioralObservation,
  CoachSessionPrepLatestGrowthRecord,
} from "@/hooks/useCoachSessionPrepData";

export type CoachSessionPrepEmergingPatternState = "strengthening" | "mixed" | "forming";

export type CoachSessionPrepEmergingPatternItem = {
  id: string;
  title: string;
  summary: string;
  state: CoachSessionPrepEmergingPatternState;
  stateLabel: string;
  whyItMatters: string;
  whatToProbe: string;
  evidenceCount: number;
  evidence: MeaningfulNoticingCard["evidence"];
};

export type CoachSessionPrepEmergingPatternsViewModel = {
  title: string;
  headline: string;
  supportingLine: string | null;
  helperText: string;
  patterns: CoachSessionPrepEmergingPatternItem[];
};

function stateFromConfidence(
  confidence: MeaningfulNoticingCard["confidence"],
): CoachSessionPrepEmergingPatternState {
  if (confidence === "high") return "strengthening";
  if (confidence === "medium") return "mixed";
  return "forming";
}

function stateLabelFromState(state: CoachSessionPrepEmergingPatternState) {
  if (state === "strengthening") return "Strengthening";
  if (state === "mixed") return "Mixed";
  return "Forming";
}

function normalizeText(value: string | null | undefined, fallback: string) {
  const normalized = value?.trim();
  return normalized?.length ? normalized : fallback;
}

function buildPatternFromNoticing(card: MeaningfulNoticingCard): CoachSessionPrepEmergingPatternItem {
  const state = stateFromConfidence(card.confidence);

  return {
    id: card.id,
    title: normalizeText(card.notice, "Emerging pattern"),
    summary: normalizeText(card.keyInsight, card.whyItMayMatter),
    state,
    stateLabel: stateLabelFromState(state),
    whyItMatters: normalizeText(card.whyItMayMatter, "More evidence is needed before this pattern is fully trustworthy."),
    whatToProbe: normalizeText(card.whatToExplore, "Keep noticing where this signal shows up next."),
    evidenceCount: card.evidence.length,
    evidence: card.evidence,
  };
}

function buildFallbackPattern(params: {
  behavioralObservation: CoachSessionPrepBehavioralObservation | null | undefined;
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
}): CoachSessionPrepEmergingPatternItem {
  return {
    id: "emerging-pattern-fallback",
    title: "Early signal still forming",
    summary: params.behavioralObservation?.reflection
      ?? params.latestCoachGrowthRecord?.direction[0]
      ?? "Coach Veera needs more repeated evidence before a stronger pattern can be named.",
    state: "forming",
    stateLabel: "Forming",
    whyItMatters: params.behavioralObservation?.moment
      ?? "There is not enough repeated evidence yet to anchor a stronger pattern read.",
    whatToProbe: params.latestCoachGrowthRecord?.nextConversationSignals[0]
      ?? "Use the next conversation to gather another clean signal.",
    evidenceCount: 0,
    evidence: [],
  };
}

export function buildCoachSessionPrepEmergingPatterns(params: {
  behavioralObservation: CoachSessionPrepBehavioralObservation | null | undefined;
  meaningfulNoticingCards: MeaningfulNoticingCard[];
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
}): CoachSessionPrepEmergingPatternsViewModel {
  const headline = normalizeText(
    params.behavioralObservation?.moment,
    "Coach Veera is still building a stable behavioral read from recent evidence.",
  );
  const supportingLine = params.behavioralObservation?.pattern
    ?? params.behavioralObservation?.reflection
    ?? params.latestCoachGrowthRecord?.direction[0]
    ?? null;
  const patterns = params.meaningfulNoticingCards.length > 0
    ? params.meaningfulNoticingCards.map(buildPatternFromNoticing)
    : [buildFallbackPattern(params)];

  return {
    title: "Emerging Patterns",
    headline,
    supportingLine,
    helperText: "Open a pattern to see what we’re noticing, why it matters, and the evidence behind it.",
    patterns,
  };
}
